import * as React from "react";
import {
  Dialog,
  Grid,
  GridSpacing,
  RowCol,
  ButtonContainer,
  Button,
  Size,
  Title,
  Paragraph,
  TextColor,
  TextAlignment,
  PrimaryButton,
} from "@root/shared";
import { locationStore } from "@root/stores";
import { action, computed, observable } from "mobx";
import { observer, inject } from "mobx-react";
import { IApp } from "@lib/common-interfaces";
import { logger } from "@root/lib/telemetry";
import { GITHUB_PR_HEAD_BRANCH, GITHUB_PR_SHA, APP_IDS } from "@root/data/build/constants";
import { GitHubAppUIStore } from "./github-app-ui-store";
import { noop } from "lodash";
const styles = require("./finishing.scss");

type BuildConfigButtonProps = { app: IApp; href: string; disabled: boolean; crossPlatform: boolean };
type BuildConfigButtonState = { clicked: boolean };

@observer
class BuildConfigButton extends React.Component<BuildConfigButtonProps, BuildConfigButtonState> {
  public state: BuildConfigButtonState = {
    clicked: false,
  };
  public componentDidUpdate(prevProps: BuildConfigButtonProps, prevState: BuildConfigButtonState) {
    if (
      (!prevProps.app.owner && this.props.app.owner) ||
      (prevProps.app.owner && this.props.app.owner && prevProps.app.owner.type !== this.props.app.owner.type) ||
      (!prevState.clicked && this.state.clicked)
    ) {
      // All the data we need to navigate to the build config modal is now available
      // If navigation was pending, do it now:
      if (this.state.clicked) {
        // Track app creation
        logger.info("Configure app", {
          scenario: "github-integration",
          crossPlatform: this.props.crossPlatform,
          os: this.props.app.os,
          platform: this.props.app.platform,
        });
        // Navigate to the build config modal
        window.location.href = this.props.href;
      }
    }
  }
  public onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault(); // don’t navigate to href target; DO allow event to bubble
    this.setState({ clicked: true });
  };
  public render() {
    const {
      crossPlatform,
      app: { os },
      disabled,
      href,
    } = this.props;
    const Tag = crossPlatform ? Button : PrimaryButton;
    return (
      <Tag data-os={os} progress={this.state.clicked} disabled={!this.state.clicked && disabled} href={href} onClick={this.onClick}>
        Configure{crossPlatform ? ` ${os}` : ""}
      </Tag>
    );
  }
}

@inject("githubAppUIStore")
@observer
export class Finishing extends React.Component<{ githubAppUIStore: GitHubAppUIStore }> {
  @computed private get title(): string {
    if (this.prHeadBranch && this.prHash) {
      return "One final step to build your pull request";
    }
    return `One final step to build your app${this.apps && this.apps.length > 1 ? "s" : ""}`;
  }

  @computed private get description(): string {
    if (this.prHeadBranch && this.prHash) {
      return "Configure the target branch of your pull request, build it and follow the progress on GitHub.";
    }
    return "Configure your branch to kick off a build. Customize your builds, run automated UI tests, and automate releasing your app to testers and users.";
  }

  @computed private get targetBranch(): string | void {
    return this.props.githubAppUIStore.finishingUIStore!.targetBranch;
  }

  @computed private get apps(): IApp[] | void {
    return this.props.githubAppUIStore.finishingUIStore!.apps;
  }

  @computed private get prHeadBranch(): string | void {
    return this.props.githubAppUIStore.finishingUIStore!.prHeadBranch!;
  }

  @computed private get prHash(): string | void {
    return this.props.githubAppUIStore.finishingUIStore!.prHash!;
  }

  private getUrlWithApp(app: IApp): string | void {
    if (!this.targetBranch || !app.owner) {
      return;
    }
    const targetBranch = encodeURIComponent(this.targetBranch);
    const query = {
      ...(this.apps &&
        this.apps.length > 1 && {
          [APP_IDS]: this.apps.map((app) => app.id),
        }),
      ...(this.prHeadBranch &&
        this.prHash && {
          [GITHUB_PR_HEAD_BRANCH]: this.prHeadBranch,
          [GITHUB_PR_SHA]: this.prHash,
        }),
    };
    return locationStore.getUrlWithApp(`/build/branches/${targetBranch}/setup`, app, query);
  }

  @observable public dismissed: boolean = false;
  @action private dismissAndStore = () => {
    // Disable buttons to prevent additional clicks
    this.dismissed = true;
  };

  public render() {
    return (
      <Dialog visible={true} onRequestClose={noop} width={470}>
        <Grid rowSpacing={GridSpacing.Large}>
          <RowCol center>
            <img
              className={styles.illustration}
              height="200"
              role="presentation"
              aria-hidden
              tabIndex={-1}
              src={require("./illustration-ready.svg")}
            />
          </RowCol>
          <RowCol center>
            <Grid rowSpacing={GridSpacing.XSmall}>
              <RowCol>
                <Title align={TextAlignment.Center} size={Size.Medium}>
                  {this.title}
                </Title>
              </RowCol>
              <RowCol>
                <Paragraph align={TextAlignment.Center} color={TextColor.Secondary} size={Size.Medium}>
                  {this.description}
                </Paragraph>
              </RowCol>
            </Grid>
          </RowCol>
          <RowCol center>
            <ButtonContainer className={styles.buttonContainer} equalize onClick={this.dismissAndStore}>
              {(this.apps || []).map((app: IApp) => (
                <BuildConfigButton
                  key={app.os}
                  crossPlatform={!!this.apps && this.apps.length > 1}
                  app={app}
                  href={this.getUrlWithApp(app) || "#"}
                  disabled={this.dismissed}
                />
              ))}
            </ButtonContainer>
          </RowCol>
        </Grid>
      </Dialog>
    );
  }
}
